import { CollectionData, CollectionItemData } from '@/types/collection';
import { ItemData, UserItemStatusData } from '@/types/item';
import { CircleData, CircleMember, CircleItemResult, CircleCollectionResult, CircleItemStatus } from '@/types/circle';
import { UserData } from '@/types/user';

// ── helpers ──────────────────────────────────────────────────────────────────

async function api<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, options);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ── Collections ───────────────────────────────────────────────────────────────

export async function fetchCollections(params?: {
  ownerId?: string;
  spaceId?: string;
  visibility?: string;
  /** Phase 9.4: pass 'collaborator' to get only shared-with-me collections */
  role?: string;
  /** Only return collections owned by the current user (excludes public collections from others) */
  mine?: boolean;
}): Promise<{ collections: CollectionData[] }> {
  const qs = new URLSearchParams(
    Object.entries(params ?? {}).filter(([, v]) => v !== undefined) as [string, string][]
  ).toString();
  return api(`/api/collections${qs ? `?${qs}` : ''}`);
}

/**
 * Phase 9.3: fetch the current user's collections that already contain a given item.
 * Used by ItemCollectionPicker to show checkmarks on collections.
 * Maps to GET /api/collections?itemId=<id>
 */
export async function fetchCollectionsContainingItem(
  itemId: string
): Promise<{ collections: CollectionData[] }> {
  return api(`/api/collections?itemId=${encodeURIComponent(itemId)}`);
}

export async function fetchCollection(id: string): Promise<{ collection: CollectionData }> {
  return api(`/api/collections/${id}`);
}

export async function fetchDefaultCollection(): Promise<{ collection: CollectionData }> {
  return api('/api/collections/default');
}

export async function fetchCollectionItems(id: string): Promise<{ items: CollectionItemData[] }> {
  return api(`/api/collections/${id}/items`);
}


export async function fetchCollaborators(id: string): Promise<{
  collaborators: Array<{
    userId?: string;
    email?: string;
    role: string;
    status: string;
    user?: UserData;
  }>;
}> {
  return api(`/api/collections/${id}/collaborators`);
}

export async function createCollection(data: {
  title: string;
  description?: string;
  visibility: string;
  isOrdered: boolean;
  spaceId?: string;
}): Promise<{ collection: CollectionData }> {
  return api('/api/collections', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateCollection(
  id: string,
  data: Partial<{ title: string; description: string; visibility: string; tags: string[] }>
): Promise<{ collection: CollectionData }> {
  return api(`/api/collections/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}


export async function addCollectionItem(
  collectionId: string,
  data: { itemId: string; position?: number }
): Promise<{ collectionItem: CollectionItemData }> {
  return api(`/api/collections/${collectionId}/items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/** Batch-create items and add them to a collection (used by list editor). */
export async function batchAddCollectionItems(
  collectionId: string,
  items: Array<{ title: string; categoryId: string; position: number }>
): Promise<{ collectionItems: CollectionItemData[] }> {
  return api(`/api/collections/${collectionId}/items/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
}

/** Batch-reorder collection items by providing the full ordered ID list. */
export async function reorderCollectionItems(
  collectionId: string,
  collectionItemIds: string[]
): Promise<{ success: boolean }> {
  return api(`/api/collections/${collectionId}/items/reorder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ collectionItemIds }),
  });
}

/** Remove a single item from a collection. */
export async function removeCollectionItem(
  collectionId: string,
  collectionItemId: string
): Promise<{ success: boolean }> {
  return api(`/api/collections/${collectionId}/items/${collectionItemId}`, {
    method: 'DELETE',
  });
}

/** Update an item's title (private items only, owner must match). */
export async function updateItem(
  itemId: string,
  data: { title?: string }
): Promise<{ item: ItemData }> {
  return api(`/api/items/${itemId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function forkTemplate(templateId: string): Promise<{ collection: CollectionData }> {
  return api(`/api/templates/${templateId}/fork`, { method: 'POST' });
}

/**
 * Phase 9.7: Shallow-copy a public/unlisted collection into the current user's library.
 * Maps to POST /api/collections/[id]/copy
 */
export async function copyCollection(collectionId: string): Promise<{ collection: CollectionData }> {
  return api(`/api/collections/${collectionId}/copy`, { method: 'POST' });
}

export async function deleteCollection(collectionId: string): Promise<void> {
  return api(`/api/collections/${collectionId}`, { method: 'DELETE' });
}

/**
 * Phase 9.4: Toggle pin on a collection. Returns the new pinned state.
 * Maps to POST /api/collections/[id]/pin
 */
export async function togglePin(collectionId: string): Promise<{ pinned: boolean }> {
  return api(`/api/collections/${collectionId}/pin`, { method: 'POST' });
}

/**
 * Phase 9.4: Fetch all collections the current user has pinned.
 * Maps to GET /api/users/me/pins
 */
export async function fetchMyPins(): Promise<{ collections: CollectionData[] }> {
  return api('/api/users/me/pins');
}

// ── Items ─────────────────────────────────────────────────────────────────────

// EnrichmentResult shape returned by /api/items/search
export type SearchResult = {
  externalId: string;
  title: string;
  thumbnail?: string;
  properties: Array<{ key: string; value: string; type: string; isPrimary?: boolean; source: string }>;
};

export async function searchItems(
  query: string,
  provider: string,
  options?: { mediaType?: string; countryCode?: string; categorySlug?: string }
): Promise<{ results: SearchResult[] }> {
  const params: Record<string, string> = { q: query, provider };
  if (options?.mediaType) params.mediaType = options.mediaType;
  if (options?.countryCode) params.countryCode = options.countryCode;
  if (options?.categorySlug) params.categorySlug = options.categorySlug;
  const qs = new URLSearchParams(params).toString();
  return api(`/api/items/search?${qs}`);
}

export async function fetchItem(id: string): Promise<{ item: ItemData }> {
  return api(`/api/items/${id}`);
}

export async function createItem(data: {
  title: string;
  categoryId: string;
  scope?: string;
  sourceRef?: { provider: string; externalId: string };
  properties?: Array<{ key: string; value: string; type: string; isPrimary?: boolean; source: string }>;
  thumbnail?: string;
  mediaType?: 'movie' | 'tv';
}): Promise<{ item: ItemData }> {
  return api('/api/items', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// ── UserItemStatus ────────────────────────────────────────────────────────────

/**
 * Fetch the current user's status for a single item.
 * Returns all-false defaults + exists: false when no record exists yet.
 * Maps to GET /api/items/[itemId]/status
 */
export async function fetchUserItemStatus(
  itemId: string
): Promise<{ status: UserItemStatusData; exists: boolean }> {
  return api(`/api/items/${itemId}/status`);
}

/**
 * Fetch the current user's status for multiple items in a single request.
 * Replaces N individual fetchUserItemStatus calls (e.g. from discovery panels).
 * Maps to GET /api/items/status/batch?ids=id1,id2,...
 *
 * Returns a map of itemId → status (with all-false defaults for missing records).
 */
export async function fetchUserItemStatuses(
  itemIds: string[]
): Promise<{ statuses: Record<string, UserItemStatusData & { exists: boolean }> }> {
  if (itemIds.length === 0) return { statuses: {} };
  const qs = itemIds.map(encodeURIComponent).join(',');
  return api(`/api/items/status/batch?ids=${qs}`);
}

/**
 * Upsert partial status fields for the current user + item.
 * At least one field must be provided; omitted fields are left unchanged.
 * Maps to PATCH /api/items/[itemId]/status
 */
export async function updateUserItemStatus(
  itemId: string,
  data: Partial<
    Pick<UserItemStatusData, 'wantToDo' | 'done' | 'notInterested' | 'rating' | 'reviewText'>
  >
): Promise<{ status: UserItemStatusData }> {
  return api(`/api/items/${itemId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// ── Categories ────────────────────────────────────────────────────────────────

export interface CategoryData {
  _id: string;
  name: string;
  slug: string;
  icon?: string;
}

export async function fetchCategories(): Promise<{ categories: CategoryData[] }> {
  return api('/api/categories');
}

export async function fetchItemNetwork(id: string): Promise<import('@/app/api/items/[itemId]/network/route').NetworkResult> {
  return api(`/api/items/${id}/network`);
}

export async function fetchItemNetworkCounts(itemIds: string[]): Promise<{ counts: Record<string, number> }> {
  return api('/api/items/network-counts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ itemIds }),
  });
}

// ── Circles ───────────────────────────────────────────────────────────────────

export async function fetchCircles(): Promise<{ circles: CircleData[] }> {
  return api('/api/circles');
}

export async function fetchCircle(id: string): Promise<{ circle: CircleData }> {
  return api(`/api/circles/${id}`);
}

export async function createCircle(data: {
  name: string;
  memberIds?: string[];
  searchDefaults?: {
    categoryId?: string;
    resultType?: 'items' | 'collections';
    tags?: { value: string }[];
    limit?: number;
    propertyFilters?: { key: string; value: string }[];
  };
}): Promise<{ circle: CircleData }> {
  return api('/api/circles', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateCircle(
  id: string,
  data: Partial<{
    name: string;
    description: string;
    searchDefaults: {
      categoryId?: string | null;
      propertyFilters?: { key: string; value: string }[];
    };
  }>
): Promise<{ circle: CircleData }> {
  return api(`/api/circles/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteCircle(id: string): Promise<void> {
  return api(`/api/circles/${id}`, { method: 'DELETE' });
}

export async function addCircleMember(
  circleId: string,
  userId: string
): Promise<{ member: CircleMember }> {
  return api(`/api/circles/${circleId}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
}

export async function removeCircleMember(
  circleId: string,
  userId: string
): Promise<void> {
  return api(`/api/circles/${circleId}/members`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
}

export async function reorderCircleMembers(
  circleId: string,
  memberIds: string[]
): Promise<{ circle: CircleData }> {
  return api(`/api/circles/${circleId}/members/reorder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ memberIds }),
  });
}

/**
 * S4.1 — Generates a shareable invite link for a circle.
 * Optionally sends an email to the specified address.
 * Returns the full invite URL and the raw token.
 */
export async function generateCircleInviteLink(
  circleId: string,
  email?: string
): Promise<{ inviteUrl: string; token: string }> {
  return api(`/api/circles/${circleId}/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(email ? { email } : {}),
  });
}

export async function fetchDiscovery(
  id: string,
  params: {
    resultType?: string;
    categoryId?: string;
    limit?: number;
    /** Zero-based offset for pagination. */
    offset?: number;
    query?: string;
    /** Property filters serialized as "genre:Horror,genre:Thriller" */
    propertyFilters?: { key: string; value: string }[];
  }
): Promise<{ results: CircleItemResult[] | CircleCollectionResult[]; resultType: string; offset?: number; limit?: number }> {
  const { propertyFilters, ...scalarParams } = params;

  const queryEntries: [string, string][] = Object.entries(scalarParams)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => [k, String(v)]);

  // Serialize propertyFilters as comma-separated "key:value" pairs
  if (propertyFilters && propertyFilters.length > 0) {
    queryEntries.push([
      'propertyFilters',
      propertyFilters.map((f) => `${f.key}:${f.value}`).join(','),
    ]);
  }

  const qs = new URLSearchParams(queryEntries).toString();
  return api(`/api/circles/${id}/discover${qs ? `?${qs}` : ''}`);
}

// ── Circle preview ───────────────────────────────────────────────────────────

export interface CirclePreviewItem {
  itemId: string;
  title: string;
  thumbnail: string | null;
  topMemberName: string | null;
}

export async function fetchCirclePreview(
  circleId: string
): Promise<{ items: CirclePreviewItem[] }> {
  return api(`/api/circles/${circleId}/preview`);
}

// ── Circle property tag suggestions ──────────────────────────────────────────

/**
 * Fetch distinct property values from members' items (for the edit/detail page).
 * Requires the user to be a circle member.
 */
export async function fetchCircleTags(
  circleId: string,
  categoryId: string,
  key = 'genre'
): Promise<{ values: Array<{ value: string; count: number }> }> {
  const qs = new URLSearchParams({ categoryId, key }).toString();
  return api(`/api/circles/${circleId}/tags?${qs}`);
}

/**
 * Fetch distinct property values from all global items in a category.
 * Used during circle creation (no members yet).
 */
export async function fetchCategoryPropertyValues(
  categoryId: string,
  key = 'genre'
): Promise<{ values: Array<{ value: string; count: number }> }> {
  const qs = new URLSearchParams({ key }).toString();
  return api(`/api/categories/${categoryId}/property-values?${qs}`);
}

export async function fetchItemStatus(
  circleId: string,
  itemId: string
): Promise<{ status: CircleItemStatus }> {
  return api(`/api/circles/${circleId}/items/${itemId}/status`);
}

export async function updateItemStatus(
  circleId: string,
  itemId: string,
  data: Partial<{ wantToDo: boolean; done: boolean; notInterested: boolean }>
): Promise<{ status: CircleItemStatus }> {
  return api(`/api/circles/${circleId}/items/${itemId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/** Search for users. Pass open=true to search all users (follow context). */
export async function searchUsers(q: string, open = false): Promise<{
  users: Array<{ _id: string; displayName: string; username: string; imageUrl?: string }>;
}> {
  const params = new URLSearchParams({ q });
  if (open) params.set('open', 'true');
  return api(`/api/users/search?${params}`);
}

// ── Follow ────────────────────────────────────────────────────────────────────

/**
 * Phase 9.5: Follow a user (open follow — immediately accepted).
 * Maps to POST /api/users/[userId]/follow
 */
export async function followUser(userId: string): Promise<{ following: boolean }> {
  return api(`/api/users/${userId}/follow`, { method: 'POST' });
}

/**
 * Phase 9.5: Unfollow a user.
 * Maps to DELETE /api/users/[userId]/follow
 */
export async function unfollowUser(userId: string): Promise<{ following: boolean }> {
  return api(`/api/users/${userId}/follow`, { method: 'DELETE' });
}

/**
 * Phase 9.5: Fetch all users the current user follows.
 * Maps to GET /api/users/me/following
 */
export async function fetchFollowing(): Promise<{
  users: Array<{ _id: string; displayName: string; username: string; imageUrl?: string }>;
}> {
  return api('/api/users/me/following');
}

/** Shape of a single item in the following feed */
export interface FollowingFeedEntry {
  collectionItemId: string;
  itemId: string;
  title: string;
  thumbnail?: string;
  categorySlug?: string;
  collectionId: string;
  collectionTitle: string;
  addedBy: string;
  addedByDisplayName: string;
  addedByImageUrl?: string;
  addedAt: string;
}

/**
 * Phase 9.5: Fetch recent items added by followed users.
 * Maps to GET /api/users/me/following/feed
 */
export async function fetchFollowingFeed(limit = 20): Promise<{
  items: FollowingFeedEntry[];
}> {
  return api(`/api/users/me/following/feed?limit=${limit}`);
}

// ── Users ─────────────────────────────────────────────────────────────────────

export interface TasteProfile {
  userId: string;
  tags: string[];
  summary: string;
  categoryBreakdown: Array<{ categoryId: string; name: string; count: number }>;
  topSignals: Array<{ key: string; value: string; count: number }>;
}

export async function fetchTasteProfile(userId: string): Promise<{ profile: TasteProfile }> {
  return api(`/api/users/${userId}/taste-profile`);
}

export interface UserPreferences {
  countryCode: string;
  theme: 'light' | 'dark' | 'system';
  defaultCollectionVisibility: 'public' | 'private' | 'unlisted';
  emailNotifications: {
    circleDigest: boolean;
    collaboratorInvites: boolean;
    newFollowers: boolean;
  };
}

export async function fetchCurrentUserPreferences(): Promise<{ user: { preferences: UserPreferences } }> {
  return api('/api/users/me');
}

/** Update the current user's own profile fields (e.g. mark onboarding complete). */
export async function updateCurrentUser(data: {
  onboardingComplete?: boolean;
  preferences?: Partial<UserPreferences & {
    emailNotifications?: Partial<UserPreferences['emailNotifications']>;
  }>;
}): Promise<{ user: { onboardingComplete: boolean; preferences: UserPreferences } }> {
  return api('/api/users/me', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// ── Templates ─────────────────────────────────────────────────────────────────

export interface TemplateData {
  _id: string;
  title: string;
  description?: string;
  templateCategory?: string;
  tags: string[];
  stats: { copyCount: number };
  itemCount?: number;
}

export async function fetchTemplates(params?: { category?: string }): Promise<{ templates: TemplateData[] }> {
  const qs = params?.category ? `?category=${params.category}` : '';
  return api(`/api/templates${qs}`);
}

// ── Collaborators ─────────────────────────────────────────────────────────────

/**
 * Invite a collaborator to a collection by userId or email.
 * Maps to POST /api/collections/:id/collaborators
 */
export async function inviteCollaborator(
  collectionId: string,
  data:
    | { type: 'user'; userId: string; role: string; note?: string }
    | { type: 'email'; email: string; role: string; note?: string }
): Promise<{ collaborator: { userId?: string; email?: string; role: string; status: string } }> {
  return api(`/api/collections/${collectionId}/collaborators`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/**
 * Update a collaborator's role.
 * Maps to PATCH /api/collections/:id/collaborators/:userId { role }
 */
export async function updateCollaboratorRole(
  collectionId: string,
  userId: string,
  role: string
): Promise<void> {
  return api(`/api/collections/${collectionId}/collaborators/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
}

/**
 * Remove a collaborator (or self-leave) from a collection.
 * Maps to DELETE /api/collections/:id/collaborators/:userId
 */
export async function removeCollaborator(collectionId: string, userId: string): Promise<void> {
  return api(`/api/collections/${collectionId}/collaborators/${userId}`, { method: 'DELETE' });
}

// ── Library ───────────────────────────────────────────────────────────────────

export interface MyItemsParams {
  category?: string;
  status?: string;
  noList?: boolean;
  page?: number;
}

export interface MyItemEntry {
  itemId: string;
  lists: { collectionId: string; collectionTitle: string }[];
  title: string;
  thumbnail?: string;
  categoryId: string;
  categoryName?: string;
  categorySlug?: string;
  properties?: Array<{ key: string; value: string }>;
  addedAt: string;
  itemStatus: { wantToDo: boolean; done: boolean; notInterested: boolean };
}

/**
 * Cross-collection view of all items the current user has saved.
 * Maps to GET /api/users/me/items
 */
export async function fetchMyItems(params?: MyItemsParams): Promise<{
  items: MyItemEntry[];
  pagination: { total: number; page: number; pageSize: number; totalPages: number };
}> {
  const qs = new URLSearchParams(
    Object.entries(params ?? {})
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, String(v)])
  ).toString();
  return api(`/api/users/me/items${qs ? `?${qs}` : ''}`);
}

/**
 * Remove an item from the current user's library (all their lists).
 * The Item doc is deleted only if no CollectionItem references remain globally.
 * Maps to DELETE /api/users/me/items/[itemId]
 */
export async function deleteMyItem(
  itemId: string
): Promise<{ removed: number; itemDeleted: boolean }> {
  return api(`/api/users/me/items/${encodeURIComponent(itemId)}`, { method: 'DELETE' });
}

// ── Invites ───────────────────────────────────────────────────────────────────

export async function fetchPendingInvites(): Promise<{
  invites: Array<{
    collection: CollectionData & { _id: string };
    collaborator: { role: string; invitedAt?: string; status: string };
  }>;
}> {
  return api('/api/invites/pending');
}

export async function respondToInvite(
  collectionId: string,
  userId: string,
  action: 'accept' | 'reject'
): Promise<void> {
  return api(`/api/collections/${collectionId}/collaborators/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
}

// ── Follow Invites ─────────────────────────────────────────────────────────────

export type FollowInviteStatus = 'followed' | 'already_following' | 'invited' | 'invite_accepted';

/**
 * Follow an existing user by userId, or invite someone by email.
 * message is optional (max 255 chars) and included in the invite email.
 */
export async function sendFollowInvite(params: {
  userId?: string;
  email?: string;
  message?: string;
  circleId?: string;
}): Promise<{ status: FollowInviteStatus }> {
  return api('/api/users/me/follow-invite', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export interface PendingFollowInviteEntry {
  _id: string;
  email: string;
  message?: string;
  createdAt: string;
}

/** Fetch pending follow invites the current user has sent */
export async function fetchPendingFollowInvites(): Promise<{ invites: PendingFollowInviteEntry[] }> {
  return api('/api/users/me/follow-invite/pending');
}
